import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowRight, BarChart3, LayoutGrid, List } from "lucide-react";
import {
  readLayout,
  visibleReports,
  writeLayout,
  type CatalogLayout,
  type ReportDefinition,
} from "@/lib/report-catalog";
import { useSession } from "@/components/session-provider";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/reports/")({
  component: ReportCatalogPage,
});

/**
 * The reports catalog.
 *
 * ⚠️ **A list, not a redirect to the only report we have.** With one report a catalog looks
 * like scaffolding, and it was deliberately deferred once for that reason (`BL-030`). What
 * changed the answer is that a reader who lands straight in a report has no way to learn what
 * else exists or what any of it counts — and D-53 already has five rates that do not share a
 * denominator. A name with no statement of the question it answers is how one figure gets read
 * two ways.
 *
 * ⚠️ **Every card states what the report answers**, not just what it is called. "Request
 * Activity" tells a reader almost nothing on its own.
 *
 * ⚠️ Cards are only advertised where the reader holds one of the reporting permissions, and
 * that is a courtesy rather than a control — the report's own page and every endpoint behind it
 * re-check server-side, and the rows are pinned to the caller's tier regardless.
 */
function ReportCatalogPage() {
  const { can } = useSession();

  const [layout, setLayout] = useState<CatalogLayout>(() => readLayout());

  const reports = visibleReports(can);

  const choose = (next: CatalogLayout) => {
    setLayout(next);
    writeLayout(next);
  };

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 p-4 sm:p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Reports</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {/*
              ⚠️ Said once, here, rather than on every card. Two readers with access to
              different queues see different totals in any request report below, and both are
              right — stating it at the door is cheaper than explaining it after somebody has
              compared two screenshots.

              ⚠️ Worded "what you can already see" rather than "the requests you can already
              see", which is what it said when every report on this surface was about requests.
              "Users Without a Site" reports on PEOPLE and is scoped by a different permission
              entirely, so the narrower sentence had become a claim this page cannot make.
            */}
            Every report covers only what you can already see, so two people with different access
            will see different totals.
          </p>
        </div>

        <div
          className="flex items-center gap-0.5 rounded-lg border border-border p-0.5"
          role="group"
          aria-label="Layout"
        >
          <LayoutButton
            active={layout === "cards"}
            onClick={() => choose("cards")}
            label="Cards"
            icon={<LayoutGrid className="h-3.5 w-3.5" aria-hidden />}
          />
          <LayoutButton
            active={layout === "list"}
            onClick={() => choose("list")}
            label="List"
            icon={<List className="h-3.5 w-3.5" aria-hidden />}
          />
        </div>
      </header>

      {reports.length === 0 ? (
        <p className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
          No reports are available to you yet.
        </p>
      ) : layout === "cards" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {reports.map((report) => (
            <ReportCard key={report.id} report={report} />
          ))}
        </div>
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
          {reports.map((report) => (
            <ReportRow key={report.id} report={report} />
          ))}
        </ul>
      )}
    </div>
  );
}

function LayoutButton({
  active,
  onClick,
  label,
  icon,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  icon: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "flex h-8 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium transition-colors",
        active
          ? "bg-secondary text-secondary-foreground"
          : "text-muted-foreground hover:bg-muted hover:text-foreground",
      )}
    >
      {icon}
      {label}
    </button>
  );
}

/**
 * ⚠️ The whole card is the link, not a button in the corner of it. A card that looks clickable
 * and is not is the most common small frustration in a list like this, and on a phone the card
 * is the only comfortable hit target.
 */
function ReportCard({ report }: { report: ReportDefinition }) {
  return (
    <Link
      to={report.to}
      className="group flex flex-col rounded-xl border border-border bg-card p-4 transition-colors hover:border-ring hover:bg-muted/40 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:outline-none"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary text-secondary-foreground">
          <BarChart3 className="h-4.5 w-4.5" aria-hidden />
        </span>

        <div className="min-w-0 flex-1">
          <h2 className="font-medium">{report.name}</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">{report.summary}</p>
        </div>

        <ArrowRight
          className="mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
          aria-hidden
        />
      </div>

      <p className="mt-3 text-sm">
        <span className="text-muted-foreground">Answers: </span>
        {report.answers}
      </p>

      <ul className="mt-3 space-y-1">
        {report.highlights.map((highlight) => (
          <li key={highlight} className="flex gap-2 text-xs text-muted-foreground">
            <span aria-hidden>·</span>
            {highlight}
          </li>
        ))}
      </ul>
    </Link>
  );
}

function ReportRow({ report }: { report: ReportDefinition }) {
  return (
    <li>
      <Link
        to={report.to}
        className="group flex items-center gap-3 p-4 transition-colors hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none"
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-secondary text-secondary-foreground">
          <BarChart3 className="h-4 w-4" aria-hidden />
        </span>

        <div className="min-w-0 flex-1">
          <h2 className="font-medium">{report.name}</h2>
          {/*
            The question, not the highlights. A list view is for scanning, and the one line
            worth keeping at this density is what the report actually answers.
          */}
          <p className="mt-0.5 truncate text-sm text-muted-foreground">{report.answers}</p>
        </div>

        <ArrowRight
          className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
          aria-hidden
        />
      </Link>
    </li>
  );
}
